import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Logger from "../../services/Logger";
import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import UtilService from "../../services/UtilService";
import { ADMIN_CRM_REFERRAL_PATH } from "../crm/crmRoutes";
import ReferralService from "./ReferralService";
import { getInviterColumns } from "./getInviterColumns";

export default function ReferralInvitersPage() {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [pageModel, setPageModel] = useState({ page: 0, pageSize: 25 });
    const [sort, setSort] = useState([{ field: "createdAt", sort: "desc" }]);
    const [rowCount, setRowCount] = useState(0);
    const [rows, setRows] = useState([]);
    const [selectedInviter, setSelectedInviter] = useState(null);
    const [invites, setInvites] = useState([]);
    const [loadingInvites, setLoadingInvites] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);

    useEffect(() => {
        if (eventId) {
            setLoading(true);
            setError(null);
            ReferralService.getEvent(eventId)
                .then((eventData) => {
                    setEvent(eventData);
                    setLoading(false);
                })
                .catch((err) => {
                    Logger.error("Error fetching event:", err);
                    setError(err.message || "Failed to load event");
                    setLoading(false);
                });
        }
    }, [eventId]);

    useEffect(() => {
        if (eventId) {
            ReferralService.listInviters(pageModel, sort, setRows, setRowCount, eventId, null);
        }
    }, [pageModel, sort, eventId]);

    const handleSort = (model) => {
        setSort(model);
    };

    const handleBackClick = () => {
        navigate(ADMIN_CRM_REFERRAL_PATH);
    };

    const handleRowClick = async (params) => {
        setSelectedInviter(params.row);
        setOpenDialog(true);
        setLoadingInvites(true);
        setInvites([]);
        try {
            const invitesData = await ReferralService.getInvitesByInviterId(eventId, params.row.id);
            setInvites(Array.isArray(invitesData) ? invitesData : []);
        } catch (err) {
            Logger.error("Error fetching invites:", err);
            setInvites([]);
        } finally {
            setLoadingInvites(false);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedInviter(null);
        setInvites([]);
    };

    return (
        <Box sx={{ flexGrow: 1, gap: 2, display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={handleBackClick} sx={{ mr: 2 }}>
                    Back
                </Button>
                <Typography variant="h5">
                    Referral Inviters {event ? `- ${event.name}` : ""}
                </Typography>
            </Box>

            {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Box sx={{ p: 2 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            )}

            {!loading && !error && (
                <Grid container spacing={2}>
                    {/* Inviters List */}
                    <Grid size={{ xs: 12 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Inviters List
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <DataGrid
                                    rows={rows}
                                    columns={getInviterColumns()}
                                    rowCount={rowCount}
                                    pageSizeOptions={[25, 50, 100]}
                                    paginationModel={pageModel}
                                    onPaginationModelChange={(m) => setPageModel(m)}
                                    disableSelectionOnClick
                                    experimentalFeatures={{ newEditingApi: true }}
                                    paginationMode={"server"}
                                    onRowClick={handleRowClick}
                                    onSortModelChange={handleSort}
                                    columnVisibilityModel={{ id: false }}
                                    autoHeight={true}
                                    hideFooterSelectedRowCount
                                    getRowClassName={(params) => {
                                        return params.row.id === selectedInviter?.id ? "selected-row" : "";
                                    }}
                                    sx={{
                                        "& .selected-row": {
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            "&:hover": {
                                                backgroundColor: "rgba(25, 118, 210, 0.12)",
                                            },
                                        },
                                    }}
                                    initialState={{
                                        columns: {
                                            columnVisibilityModel: {
                                                id: false,
                                            },
                                        },
                                    }}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Inviter Details Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ m: 0, p: 2, position: "relative" }}>
                    Inviter Details
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDialog}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedInviter && (
                        <Box>
                            {/* Inviter User Information */}
                            {selectedInviter.inviterUser && (
                                <>
                                    <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                                        Inviter User Information
                                    </Typography>
                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                User ID
                                            </Typography>
                                            <Typography variant="body1">{selectedInviter.inviterUser.id || "-"}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Email
                                            </Typography>
                                            <Typography variant="body1">{selectedInviter.inviterUser.email || "-"}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Name
                                            </Typography>
                                            <Typography variant="body1">
                                                {[selectedInviter.inviterUser.firstName, selectedInviter.inviterUser.lastName]
                                                    .filter(Boolean)
                                                    .join(" ") || "-"}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                User Role
                                            </Typography>
                                            <Chip
                                                label={selectedInviter.inviterUser.userRole || "-"}
                                                size="small"
                                                color={selectedInviter.inviterUser.userRole === "ADMIN" ? "primary" : "default"}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Pay Plan
                                            </Typography>
                                            <Chip
                                                label={UtilService.resolveWebPayPlan(selectedInviter.inviterUser) || "-"}
                                                size="small"
                                                color={UtilService.isStandardPayPlanForUser(selectedInviter.inviterUser) ? "success" : "default"}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                User Status
                                            </Typography>
                                            <Chip
                                                label={selectedInviter.inviterUser.userStatus || "-"}
                                                size="small"
                                                color={selectedInviter.inviterUser.userStatus === "CONFIRMED" ? "success" : "default"}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Email Verified
                                            </Typography>
                                            <Chip
                                                label={selectedInviter.inviterUser.emailVerified ? "Yes" : "No"}
                                                size="small"
                                                color={selectedInviter.inviterUser.emailVerified ? "success" : "default"}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Language
                                            </Typography>
                                            <Typography variant="body1">
                                                {selectedInviter.inviterUser.details?.language || "-"}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Created At
                                            </Typography>
                                            <Typography variant="body1">
                                                {selectedInviter.inviterUser.createdAt
                                                    ? UtilService.formatDate(selectedInviter.inviterUser.createdAt)
                                                    : selectedInviter.inviterUser.formattedDate || "-"}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Divider sx={{ my: 2 }} />
                                </>
                            )}
                            {/* Referral Statistics */}
                            <Typography variant="h6" gutterBottom>
                                Referral Statistics
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Accepted Count
                                    </Typography>
                                    <Typography variant="body1">{selectedInviter.acceptedCount || 0}</Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Pending Count
                                    </Typography>
                                    <Typography variant="body1">{selectedInviter.pendingCount || 0}</Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Reward Granted
                                    </Typography>
                                    <Chip
                                        label={selectedInviter.rewardGranted ? "Yes" : "No"}
                                        color={selectedInviter.rewardGranted ? "success" : "default"}
                                        size="small"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Created At
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedInviter.createdAt
                                            ? UtilService.formatDate(selectedInviter.createdAt)
                                            : "-"}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Invites ({invites.length})
                            </Typography>
                            {loadingInvites ? (
                                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : invites.length === 0 ? (
                                <Typography color="text.secondary" sx={{ p: 2 }}>
                                    No invites found
                                </Typography>
                            ) : (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Email</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Sent At</TableCell>
                                                <TableCell>Joined At</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {invites.map((invite) => (
                                                <TableRow key={invite.id}>
                                                    <TableCell>{invite.inviteeEmail || "-"}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={invite.status?.toUpperCase() || "-"}
                                                            color={
                                                                invite.status === "active"
                                                                    ? "success"
                                                                    : invite.status === "verified"
                                                                        ? "info"
                                                                        : invite.status === "rejected"
                                                                            ? "error"
                                                                            : "default"
                                                            }
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {invite.sentAt ? UtilService.formatDate(invite.sentAt) : "-"}
                                                    </TableCell>
                                                    <TableCell>
                                                        {invite.joinedAt ? UtilService.formatDate(invite.joinedAt) : "-"}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}

